import { createTheme } from "@mui/material";

export interface ColorPalette {
  name: string;
  blueLight: string;
  blueMedium: string;
  blueDark: string;
  blueDarker: string;
  blueSubtle: string;
  patientSummaryBanner: string;
  greenLight: string;
  greenMedium: string;
  greenDark: string;
  greenSubtle: string;
  greenPastel: string;
  gradient: string;
  loginHeroGradient: string;
  loginHeroGradientMobile: string;
  loginBrandPanel: string;
  purpleLight: string;
  purpleMedium: string;
  purpleDark: string;
  redLight: string;
  redMedium: string;
  redDark: string;
  redPastel: string;
  textAlert: string;
  greyLight: string;
  greyMedium: string;
  greyDark: string;
  greyBase: string;
  greyHeading: string;
  brandTeal: string;
  darkAqua20: string;
  black: string;
  white: string;
  neutralBackground: string;
  neutral50: string;
  neutral100: string;
  neutral200: string;
  neutral300: string;
  neutral400: string;
  neutral500: string;
  neutral600: string;
  neutral700: string;
  yellowPastel: string;
  tealAccent: string;
  headerBackground: string;
  secondaryBackgroundColor: string;
  primaryColor: string;
  secondaryColor: string;
  primaryTextColor: string;
  secondaryTextColor: string;
  testOrderSecondaryText: string;
  borderColor: string;
  resultsNegativeGreen: string;
  rowNotReadGreen: string;
  rowMissingInfoRed: string;
}

export interface StyleTheme {
  borderRadius: string;
  boxShadow: string;
  largeFontSize: string;
  mediumFontSize: string;
  largeContentWidth: string;
  extraLargeContentWidth: string;
  windowMinWidthTablet: string;
  windowMinWidthDesktop: string;
}

export interface ThemePalette extends ColorPalette, StyleTheme {}

const BRAND_TEAL = "#084153";

export const themePalette: ThemePalette = {
  name: "normal",
  blueLight: "#C7E1F0",
  blueMedium: "#63CBF6",
  blueDark: "#064682",
  blueDarker: "#064682",
  blueSubtle: "#EEF5FA",
  patientSummaryBanner: "#F0FBFF",

  greenLight: "#CEEAE3",
  greenMedium: "#03CBBA",
  greenDark: "#017E72",
  greenPastel: "#EFF8F5",
  greenSubtle: "#eef8f5",

  gradient: `linear-gradient(146.85deg, rgba(82, 194, 207, 0) 49.95%, rgba(82, 194, 207, 0.5) 100.05%), linear-gradient(90deg, ${BRAND_TEAL} 0%, ${BRAND_TEAL} 100%)`,

  loginHeroGradient: `linear-gradient(81.5deg, rgba(82, 194, 207, 0) 50%, rgba(82, 194, 207, 0.5) 100%), linear-gradient(90deg, ${BRAND_TEAL} 0%, ${BRAND_TEAL} 100%)`,

  loginHeroGradientMobile: `linear-gradient(83.95deg, rgba(82, 194, 207, 0) 50%, rgba(82, 194, 207, 0.5) 100%), linear-gradient(90deg, ${BRAND_TEAL} 0%, ${BRAND_TEAL} 100%)`,

  loginBrandPanel: BRAND_TEAL,

  purpleLight: "#C7CBF0",
  purpleMedium: "#7E73B7",
  purpleDark: "#504484",

  redLight: "#EDB193",
  redMedium: "#F0604B",
  redDark: "#C0250F",
  redPastel: "#FFF2F0",
  textAlert: "#D40000",

  greyLight: "#B6B2B2",
  greyMedium: "#5D5A5A",
  greyDark: "#433F3F",
  greyBase: "#231F20",
  greyHeading: "#171717",
  brandTeal: BRAND_TEAL,
  darkAqua20: "#B8EEFF",

  black: "#000000",
  white: "#FFFFFF",

  neutralBackground: "#EEEEEE",
  neutral50: "#F9F9F9",
  neutral100: "#E7EBF2",
  neutral200: "#DBDEE6",
  neutral300: "#C2C6CC",
  neutral400: "#AAADB3",
  neutral500: "#929499",
  neutral600: "#7A7C80",
  neutral700: "#616366",
  yellowPastel: "#F8F1AE",
  tealAccent: "#52C2CF",
  headerBackground: BRAND_TEAL,
  secondaryBackgroundColor: "#0c6182",
  primaryColor: "#3BBEB3",
  secondaryColor: "#19aae2",
  primaryTextColor: "#4f4f4f",
  secondaryTextColor: "#4f4f4f",
  testOrderSecondaryText: "#474747",

  borderColor: "#3BBEB3",
  borderRadius: "5px",
  boxShadow: "0px 6px 11px rgba(0, 0, 0, 0.04)",

  resultsNegativeGreen: "#6ACC74",
  rowMissingInfoRed: "#FFF2F0",
  rowNotReadGreen: "#EFF8F5",

  largeFontSize: "1.8rem",
  mediumFontSize: "1.3rem",
  largeContentWidth: "853px;",
  extraLargeContentWidth: "1600px",
  windowMinWidthTablet: "600px",
  windowMinWidthDesktop: "1440px",
};

export const materialTheme = createTheme({
  palette: {
    primary: {
      main: themePalette.greenMedium,
      dark: themePalette.greenDark,
    },
    secondary: {
      dark: themePalette.white,
      main: themePalette.secondaryBackgroundColor,
    },
    background: {
      default: themePalette.white,
    },
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: themePalette.greyDark,
          fontFamily: "light",
        },
        arrow: {
          color: themePalette.greyDark,
        },
      },
    },
  },
});
